// @catalog F.010
/**
 * ForecastProductRow — row densa del nuevo diseño (handoff Fase 4.b).
 *
 * Spec literal:
 *   - README §2 Row anatomy + Columnas (líneas 124-144).
 *   - prototype/forecast-page.jsx líneas 366-480 (render por columna).
 *   - prototype/styles.css líneas 311-464 (.fc-tbl tbody + sub-clases).
 *
 * Sub-componentes y helpers visuales (NumBar/DciMini/Tag/Pill/MiniSparkline/
 * formatAmountCompact/urgencyToInvClass/demandLabel) extraídos en
 * `./forecast-row-cells.tsx` para mantener P7 (<500 LoC).
 *
 * Backward-compat con SupplierCard.tsx: callbacks aceptan event opcional como
 * 3er arg (SupplierCard pasa (id,name); ForecastTable adapta a (id,name,e)).
 *
 * Constraints: <500 LoC (P7); solo Stack/Box; PROHIBIDO Grid/Drawer/Dialog.
 */
import React from "react";
import { Box, Stack } from "@mui/material";
import { ArrowForward as ArrowForwardIcon } from "@mui/icons-material";
import { getProductUrgency } from "../../pages/forecast/selectors";
import {
  DciMini,
  MiniSparkline,
  NumBar,
  Pill,
  Tag,
  demandLabel,
  formatAmountCompact,
  urgencyToInvClass,
} from "./forecast-row-cells";

type Name = string | { en_US: string; es_MX: string };

export interface ForecastProductRowProduct {
  unifiedProductId: string;
  unifiedName: Name;
  unifiedCode: string | null;
  unifiedCategory?: string | null;
  existsInOdoo16: boolean;
  existsInOdoo18: boolean;
  odoo18?: { id: number } | undefined;
  totalAvailableQty?: number;
  locationsOnHandCount?: number;
  totalSales?: number;
  recentSalesAmount?: number;
  recentSalesQty?: number;
  recentSalesByMonth?: number[];
  activeForPurchase?: boolean;
  dciStoreCount?: number | null;
  dciTargetStores?: number | null;
  dciScore?: number | null;
  dciStatus?: string | null;
  demandClassification?: string | null;
  abcClassification?: string | null;
  xyzClassification?: string | null;
  abcXyzSegment?: string | null;
  matchingMethod?: string;
  matchingConfidence?: number;
  safetyStock?: number | null;
  reorderPoint?: number | null;
  seasonality?:
    | {
        outOfSeason: boolean;
        method: "keyword" | "concentration" | null;
        peakMonths: number[];
        peakLabel: string;
      }
    | undefined;
  forecastNextPeriod?: number | null;
  forecastModelUsed?: string | null;
  lastPurchaseCost?: number | null;
}

export type RowCallback = (
  id: string,
  displayName: string,
  e?: React.MouseEvent<HTMLElement>,
) => void;

export interface ForecastProductRowProps {
  product: ForecastProductRowProduct;
  selected: boolean;
  displayName: string;
  onToggleRow: (id: string) => void;
  onOpenInventory: RowCallback;
  onOpenSales: RowCallback;
  onOpenDetail: (id: string, displayName: string) => void;
  onToggleActiveForPurchase: (id: string) => void;
  hideCode?: boolean | undefined;
  hideAnalytics?: boolean | undefined;
}

const ForecastProductRowComponent: React.FC<ForecastProductRowProps> = ({
  product,
  selected,
  displayName,
  onToggleRow,
  onOpenInventory,
  onOpenSales,
  onOpenDetail,
  onToggleActiveForPurchase,
}) => {
  // getProductUrgency espera ProductData; ForecastProductRowProduct es subset estructural.
  const urgency = getProductUrgency(product as Parameters<typeof getProductUrgency>[0]);
  const invClass = urgencyToInvClass(urgency);
  const inactive = product.activeForPurchase === false;
  const id = product.unifiedProductId;

  const handleCheckboxClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    if (!inactive) onToggleRow(id);
  };
  const handleInvClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    onOpenInventory(id, displayName, e);
  };
  const handleSalesClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    onOpenSales(id, displayName, e);
  };
  const handleNameClick = () => onOpenDetail(id, displayName);
  const handlePillClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    onToggleActiveForPurchase(id);
  };

  const dem = demandLabel(product.demandClassification);
  const sugerido = product.forecastNextPeriod ?? 0;
  const confPct = Math.round((product.matchingConfidence ?? 0) * 100);
  const sales4mAmount = product.recentSalesAmount ?? 0;
  const dataUrgency =
    urgency === "critical" || urgency === "warn" ? urgency : undefined;

  return (
    <Box
      component="tr"
      className={selected ? "selected" : undefined}
      data-urgency={dataUrgency}
      data-testid={`forecast-product-row-${id}`}
      sx={{
        position: "relative",
        opacity: inactive ? 0.55 : 1,
        bgcolor: selected ? "var(--accent-soft)" : "transparent",
        "&:hover": { bgcolor: selected ? "var(--accent-soft)" : "var(--bg)" },
        "& > td": {
          padding: "9px 10px",
          borderBottom: "1px solid var(--border)",
          verticalAlign: "middle",
          fontVariantNumeric: "tabular-nums",
        },
      }}
    >
      <td
        onClick={handleCheckboxClick}
        style={{
          width: 32,
          paddingLeft: 10,
          paddingRight: 6,
          cursor: inactive ? "not-allowed" : "pointer",
        }}
      >
        <Box
          sx={{
            width: 15,
            height: 15,
            border: "1.5px solid var(--border-strong)",
            borderRadius: "3px",
            display: "grid",
            placeItems: "center",
            cursor: inactive ? "not-allowed" : "pointer",
            opacity: inactive ? 0.3 : 1,
            bgcolor: selected ? "var(--accent)" : "var(--surface)",
            borderColor: selected ? "var(--accent)" : "var(--border-strong)",
            color: "#fff",
            fontSize: 10,
            lineHeight: 1,
          }}
        >
          {selected ? "✓" : ""}
        </Box>
      </td>

      <td style={{ minWidth: 260, maxWidth: 360 }}>
        <Stack direction="row" alignItems="center" gap="8px" sx={{ minWidth: 0 }}>
          <Box
            component="span"
            onClick={handleNameClick}
            data-testid={`forecast-row-name-${id}`}
            sx={{
              fontWeight: 500,
              cursor: "pointer",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              flex: 1,
              minWidth: 0,
              color: "var(--ink-1)",
              "&:hover": { color: "var(--accent)", textDecoration: "underline" },
            }}
            title="Ver detalle"
          >
            {displayName}
          </Box>
          <Box
            component="button"
            type="button"
            onClick={handleNameClick}
            data-testid={`forecast-row-action-${id}`}
            sx={{
              opacity: 0,
              border: 0,
              background: "transparent",
              cursor: "pointer",
              color: "var(--ink-3)",
              padding: "2px",
              transition: "opacity 0.15s",
              "tr:hover &": { opacity: 1 },
              "tr.selected &": { opacity: 1 },
              "&:hover": { color: "var(--accent)", bgcolor: "var(--bg-2)", borderRadius: "4px" },
            }}
            title="Ver detalle completo"
          >
            <ArrowForwardIcon sx={{ fontSize: 12 }} />
          </Box>
        </Stack>
        <Stack
          direction="row"
          gap="8px"
          alignItems="center"
          sx={{ fontSize: 11, color: "var(--ink-3)", mt: "2px" }}
        >
          <Box
            component="code"
            sx={{
              fontFamily: "var(--font-mono)",
              fontSize: 10.5,
              bgcolor: "var(--bg-2)",
              padding: "1px 5px",
              borderRadius: "3px",
            }}
          >
            {product.unifiedCode ?? "—"}
          </Box>
          {product.unifiedCategory && (
            <>
              <Box component="span" sx={{ color: "var(--ink-4)" }}>·</Box>
              <Box
                component="span"
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: 200,
                }}
              >
                {product.unifiedCategory}
              </Box>
            </>
          )}
        </Stack>
      </td>

      <td
        style={{ textAlign: "right", cursor: "pointer" }}
        onClick={handleInvClick}
        data-testid={`forecast-row-inv-${id}`}
      >
        <NumBar
          value={product.totalAvailableQty ?? 0}
          reorder={product.reorderPoint ?? null}
          invClass={invClass}
        />
      </td>

      <td
        style={{ textAlign: "right", cursor: "pointer" }}
        onClick={handleSalesClick}
        data-testid={`forecast-row-sales-${id}`}
      >
        <Stack direction="row" alignItems="center" justifyContent="flex-end" gap="8px">
          <Box component="span" sx={{ fontWeight: 500, fontSize: 13, fontVariantNumeric: "tabular-nums" }}>
            {formatAmountCompact(sales4mAmount)}
          </Box>
          {product.recentSalesByMonth && product.recentSalesByMonth.length > 1 && (
            <MiniSparkline data={product.recentSalesByMonth.slice(-12)} />
          )}
        </Stack>
      </td>

      <td>
        <DciMini
          score={product.dciScore ?? null}
          count={product.dciStoreCount ?? null}
          target={product.dciTargetStores ?? null}
        />
      </td>

      <td>
        <Tag mono>{product.abcXyzSegment ?? "—"}</Tag>
      </td>

      <td>
        <Tag tone={dem.tone}>{dem.label}</Tag>
      </td>

      <td>
        <Tag mono>{product.matchingMethod ?? "—"}</Tag>
      </td>

      <td style={{ textAlign: "right" }}>
        <Box
          component="span"
          sx={{
            fontWeight: 600,
            color: sugerido > 0 ? "var(--accent)" : "var(--ink-3)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {sugerido}
        </Box>
      </td>

      <td style={{ textAlign: "right" }}>
        <Box component="span" sx={{ color: "var(--ink-3)", fontVariantNumeric: "tabular-nums" }}>
          {confPct}%
        </Box>
      </td>

      <td>
        <Box
          component="button"
          type="button"
          onClick={handlePillClick}
          data-testid={`forecast-row-pill-${id}`}
          sx={{
            border: 0,
            background: "transparent",
            padding: 0,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
          aria-label={
            product.activeForPurchase === false
              ? "Activar compras"
              : "Desactivar compras"
          }
        >
          <Pill active={product.activeForPurchase !== false} />
        </Box>
      </td>
    </Box>
  );
};

export const ForecastProductRow = React.memo(
  ForecastProductRowComponent,
  (prev, next) => {
    return (
      prev.product === next.product &&
      prev.selected === next.selected &&
      prev.displayName === next.displayName
    );
  },
);

ForecastProductRow.displayName = "ForecastProductRow";
